<template lang="pug">
#send-document-remind-history-tab(v-cloak)
  .register-body.mt-4
    SendDocumentRemindTable(
      v-model="items"
      :fields="fields"
      :totalRows.sync="rows"
      :currentPage="currentPage"
      :perPage="perPage"
      :isLoading="isBusy"
      @onShowTarget="showTarget"
    )
    .table-options.d-flex.flex-row.justify-content-end.align-items-center
      // ページあたりの行数
      span.table-option-text 1ページの行数:
      .per-page
        b-form-select(
          id="per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="md"
          @change="selectPerPage"
        )
      // ページネーション
      b-pagination.m-0(
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        :disabled="isBusy"
        @change="selectPage"
        hide-goto-end-buttons
      )

  TargetFamilyDetailModal(v-model="showTargetFamilyListModal" :items="targetFamilyListItems")
  TargetOtherUserDetailModal(v-model="showTargetOtherUserListModal" :items="targetOtherUserListItems")
</template>

<script lang="ts">
import { Component, Watch, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import SendDocumentsService, { SendDocumentFamilyReceiver, SendDocumentGetModel, SendDocumentOtherUserReceiver, Type } from "@/commons/services/SendDocuments";

import SendDocumentRemindTable, { SendDocumentRemindTableField, SendDocumentRemindTableItem } from "@/pc/containers/sendDocuments/SendDocumentRemindTable.vue";
import SystemError, { HitomeqErrorType } from "@/commons/models/SystemError";

import TargetFamilyDetailModal from "@/pc/containers/sendDocuments/TargetFamilyDetailModal.vue";
import { TargetFamilyDetailTableItem } from "@/pc/containers/sendDocuments/TargetFamilyDetailTable.vue";
import TargetOtherUserDetailModal from "@/pc/containers/sendDocuments/TargetOtherUserDetailModal.vue";
import { TargetOtherUserDetailTableItem } from "@/pc/containers/sendDocuments/TargetOtherUserDetailTable.vue";
import { GroupType } from "@/commons/services/SendMessageGroups";

/**
 * ### 文書送付確認画面コンポーネント
 */
@Component({
  components: {
    SendDocumentRemindTable,
    TargetFamilyDetailModal,
    TargetOtherUserDetailModal
  }
})
export default class SendDocumentRemindHistoryTab extends Vue {
  /** 文書送付サービス */
  @Inject("SendDocumentsService") sendDocumentsService!: SendDocumentsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** ID */
  mainId: number | null = null;
  /** 文書送付 */
  sendDocuments: Array<SendDocumentGetModel> = [];

  /** テーブル列名 */
  fields: Array<SendDocumentRemindTableField> = [];
  /** テーブル各行の情報 */
  items: Array<SendDocumentRemindTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendDocumentRemind.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendDocumentRemind.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendDocumentRemind.pageRange;

  /** 宛先一覧モーダル(家族)表示可否 */
  showTargetFamilyListModal: boolean = false;
  /** 宛先一覧モーダル(家族) */
  targetFamilyListItems: Array<TargetFamilyDetailTableItem> = [];
  /** 宛先一覧モーダル(施設関係者)表示可否 */
  showTargetOtherUserListModal: boolean = false;
  /** 宛先一覧モーダル(施設関係者) */
  targetOtherUserListItems: Array<TargetOtherUserDetailTableItem> = [];

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-document-remind-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendDocumentRemindTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.rows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<SendDocumentRemindTableField> {
    let fields: Array<SendDocumentRemindTableField> = [];

    fields.push({ key: "sendDatetime", label: "再送日時", sortable: false, sortDirection: "desc", class: "send-datetime text-center" });
    fields.push({ key: "targets", label: "宛先", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "text", label: "本文", sortable: false, sortDirection: "desc", class: "text" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-document-remind-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 宛先を取得する
   * - 編集画面なので編集対象の文書送付を取得して入力要素に反映させる
   * - 既に文書送付が送信済みの場合、再度送信させてはいけないので404画面へ遷移させる
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.mainId = Number(this.$route.params.id);
    const sendDocuments: Array<SendDocumentGetModel> = await this.getSendDocuments(this.mainId);
    this.sendDocuments = sendDocuments;

    const items: Array<SendDocumentRemindTableItem> = this.createItems(sendDocuments);
    this.items = this.items.concat(items);

    this.rows = this.items.length;

    this.isBusy = false;

    return;
  }

  /**
   * ### 文書送付を取得する
   */
  public async getSendDocuments(mainId: number): Promise<Array<SendDocumentGetModel>> {
    const response: Array<SendDocumentGetModel> = await this.sendDocumentsService.get(userStore, mainId);
    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(sendDocuments: Array<SendDocumentGetModel>): Array<SendDocumentRemindTableItem> {
    let items: Array<SendDocumentRemindTableItem> = [];

    const parentSendDocument: SendDocumentGetModel = this.getParentSendDocument(sendDocuments);

    for (const sendDocument of sendDocuments) {
      if (sendDocument.subId == 0) {
        continue;
      }

      items.push({
        ...sendDocument,
        type: parentSendDocument.type
      });
    }

    const sortedItems: Array<SendDocumentRemindTableItem> = this.sortItems(items);

    return sortedItems;
  }

  /**
   * ### 最初に送信した文書送付を取得する
   *
   * - subId=0のレコードを返す
   * - 見つからない場合は例外を投げる
   */
  public getParentSendDocument(sendDocuments: Array<SendDocumentGetModel>): SendDocumentGetModel {
    let parentSendDocument: SendDocumentGetModel | null = null;

    for (const sendDocument of sendDocuments) {
      if (sendDocument.subId == 0) {
        parentSendDocument = sendDocument;
        break;
      }
    }

    if (parentSendDocument == null) {
      let error: SystemError = new SystemError("SendDocumentRemindHistoryTab::createItems() parentSendDocument is not found");
      error.url = this.$route.fullPath;
      error.hitomeqErrorType = HitomeqErrorType.SYSTEM_ERROR;
      error.statusCode = 500;

      throw error;
    }

    return parentSendDocument;
  }

  /**
   * ### 行情報をソートする
   *
   * - 送信日時の昇順でソートする
   */
  public sortItems(items: Array<SendDocumentRemindTableItem>): Array<SendDocumentRemindTableItem> {
    const sortedItems: Array<SendDocumentRemindTableItem> = items.concat().sort((a, b) => {
      const sendDatetimeA: number = a.sendDatetime as any;
      const sendDatetimeB: number = b.sendDatetime as any;

      if ((sendDatetimeA as any) < sendDatetimeB) {
        return -1;
      }
      if ((sendDatetimeA as any) > sendDatetimeB) {
        return 1;
      }
      return 0;
    });

    return sortedItems;
  }

  /**
   * ### 宛先一覧ボタン押下イベント
   */
  public showTarget(sendDocument: SendDocumentGetModel): void {
    this.setReceiverDetails(sendDocument);

    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        this.showTargetFamilyListModal = true;
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        this.showTargetOtherUserListModal = true;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          this.showTargetFamilyListModal = true;
          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          this.showTargetOtherUserListModal = true;
          break;
        }
      }
    }

    return;
  }

  /**
   * ### 宛先詳細モーダルで表示する宛先情報を格納する
   */
  public setReceiverDetails(sendDocument: SendDocumentGetModel): void {
    switch (sendDocument.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY:
      case Type.RESIDENT_GROUP: {
        let receiverDetails: Array<TargetFamilyDetailTableItem> = [];
        for (const receiver of sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
          receiverDetails.push({ ...receiver });
        }
        this.targetFamilyListItems = receiverDetails;
        break;
      }
      case Type.SELECTED_OTHER_USER:
      case Type.OTHER_USER_GROUP: {
        let receiverDetails: Array<TargetOtherUserDetailTableItem> = [];
        for (const receiver of sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
          receiverDetails.push({ ...receiver });
        }
        this.targetOtherUserListItems = receiverDetails;
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (sendDocument.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${sendDocument.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (sendDocument.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          let receiverDetails: Array<TargetFamilyDetailTableItem> = [];
          for (const receiver of sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
            receiverDetails.push({ ...receiver });
          }
          this.targetFamilyListItems = receiverDetails;
          break;
        } else if (sendDocument.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          let receiverDetails: Array<TargetOtherUserDetailTableItem> = [];
          for (const receiver of sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
            receiverDetails.push({ ...receiver });
          }
          this.targetOtherUserListItems = receiverDetails;
          break;
        }
      }
    }

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.rows = this.items.length;
    await this.createView();

    return;
  }
}
</script>
